import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MapPin, Calendar, ChevronLeft, ChevronRight, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Lightbox } from '../components/Lightbox';
import { cldPresets } from '../lib/cloudinary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveUrl = (u) => {
  if (!u) return '';
  return u.startsWith('http') ? u : `${BACKEND_URL}${u}`;
};

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [projRes, allRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/projects/${id}`),
          axios.get(`${BACKEND_URL}/api/projects`),
        ]);
        if (cancelled) return;
        setProject(projRes.data);
        // 3 "similar" projects in the same category, excluding current
        const sameCat = (allRes.data || []).filter(
          (p) => p.id !== projRes.data.id && p.category === projRes.data.category
        );
        setRelated(sameCat.slice(0, 3));
      } catch (err) {
        if (!cancelled) setError(err.response?.status === 404 ? 'Project not found' : 'Failed to load project');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    window.scrollTo(0, 0);
    return () => { cancelled = true; };
  }, [id]);

  // Deep-link: when `?img=N` is in the URL, open the lightbox at that index on mount.
  // Also keep the URL in sync as the user navigates within the lightbox (replace, not push,
  // so Back button still returns to Gallery). These hooks must run unconditionally —
  // they guard internally for the not-yet-loaded / error case.
  const imgParamEarly = searchParams.get('img');
  useEffect(() => {
    if (!project) return;
    const imgs = project.images && project.images.length > 0
      ? project.images
      : project.image ? [project.image] : [];
    if (imgParamEarly !== null && imgs.length > 0) {
      const n = parseInt(imgParamEarly, 10);
      if (!Number.isNaN(n) && n >= 0 && n < imgs.length) setLightboxIndex(n);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgParamEarly, project]);

  useEffect(() => {
    if (!project) return;
    if (lightboxIndex === null) {
      if (imgParamEarly !== null) setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ img: String(lightboxIndex) }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxIndex, project]);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <p className="text-gray-500">Loading project…</p>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen pt-24 flex flex-col items-center justify-center gap-4">
        <p className="text-gray-600">{error || 'Project not available'}</p>
        <Link to="/gallery"><Button variant="outline" style={{ borderColor: '#00CED1', color: '#00CED1' }}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Gallery
        </Button></Link>
      </div>
    );
  }

  const images = project.images && project.images.length > 0
    ? project.images
    : project.image ? [project.image] : [];
  const overlays = project.imageOverlays || [];

  const imgParam = searchParams.get('img');

  const openLightbox = (idx) => setLightboxIndex(idx);
  const closeLightbox = () => setLightboxIndex(null);
  const prev = () => setLightboxIndex((i) => (i > 0 ? i - 1 : images.length - 1));
  const next = () => setLightboxIndex((i) => (i < images.length - 1 ? i + 1 : 0));

  const heroImage = images[0];

  return (
    <div className="min-h-screen pt-20" data-testid={`project-detail-${project.id}`}>
      {/* Hero image */}
      {heroImage && (
        <section className="relative h-[60vh] min-h-[400px] w-full overflow-hidden bg-gray-900">
          <img
            src={cldPresets.galleryLightbox(resolveUrl(heroImage))}
            alt={project.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-8 md:p-12 max-w-7xl mx-auto">
            <Button
              variant="ghost"
              className="text-white hover:bg-white/10 mb-4 -ml-3"
              onClick={() => navigate('/gallery')}
              data-testid="project-detail-back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" /> Gallery
            </Button>
            <Badge
              className="capitalize mb-3 text-white border-0"
              style={{ backgroundColor: '#00CED1' }}
            >
              {project.category}
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-3 drop-shadow-lg">
              {project.title}
            </h1>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-white/90 text-sm">
              {project.location && (
                <span className="flex items-center gap-1.5">
                  <MapPin className="h-4 w-4" /> {project.location}
                </span>
              )}
              {project.year && (
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-4 w-4" /> {project.year}
                </span>
              )}
              <span>{images.length} image{images.length === 1 ? '' : 's'}</span>
            </div>
          </div>
        </section>
      )}

      {/* Description */}
      {project.description && (
        <section className="py-12 px-4">
          <div className="max-w-3xl mx-auto">
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed whitespace-pre-line">
              {project.description}
            </p>
          </div>
        </section>
      )}

      {/* Quote CTA */}
      <section className="px-4 pb-4">
        <div className="max-w-3xl mx-auto">
          <div
            className="rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 shadow-lg"
            style={{ background: 'linear-gradient(135deg, #00CED1 0%, #00a8ab 100%)' }}
          >
            <div className="text-white">
              <h3 className="text-xl md:text-2xl font-bold mb-1">
                Love this {project.category} project?
              </h3>
              <p className="text-white/90 text-sm md:text-base">
                Tell us about your vision and we'll design something equally striking for you.
              </p>
            </div>
            <Button
              onClick={() => {
                // Record analytics (fire & forget — don't block navigation)
                axios.post(`${BACKEND_URL}/api/projects/${project.id}/quote-request`).catch(() => {});
                navigate('/contact', {
                  state: {
                    projectReference: {
                      id: project.id,
                      title: project.title,
                      category: project.category,
                    },
                  },
                });
              }}
              size="lg"
              className="bg-white hover:bg-gray-100 font-semibold whitespace-nowrap flex-shrink-0"
              style={{ color: '#00CED1' }}
              data-testid="project-detail-quote-cta"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Request a quote for similar work
            </Button>
          </div>
        </div>
      </section>

      {/* Masonry gallery of all project images */}
      {images.length > 0 && (
        <section className="pb-16 px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">Gallery</h2>
            <div className="columns-1 md:columns-2 lg:columns-3 gap-4">
              {images.map((img, idx) => (
                <div
                  key={idx}
                  className="break-inside-avoid mb-4 group cursor-pointer relative overflow-hidden rounded-lg shadow-md hover:shadow-2xl transition-all duration-500 bg-gray-100"
                  onClick={() => openLightbox(idx)}
                  data-testid={`project-detail-image-${idx}`}
                >
                  <img
                    src={cldPresets.galleryCard(resolveUrl(img))}
                    alt={`${project.title} ${idx + 1}`}
                    loading="lazy"
                    className="block w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {overlays[idx] && (
                    <div className="absolute inset-x-0 bottom-0 px-4 py-2 bg-gradient-to-t from-black/80 to-transparent pointer-events-none">
                      <p className="text-white font-semibold text-sm drop-shadow">{overlays[idx]}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related / Similar projects */}
      {related.length > 0 && (
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold mb-8">Similar {project.category} projects</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((r) => {
                const rImg = (r.images && r.images[0]) || r.image || '';
                return (
                  <Link
                    key={r.id}
                    to={`/gallery/${r.id}`}
                    className="group block"
                    data-testid={`project-detail-related-${r.id}`}
                  >
                    <div className="relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 aspect-[4/3]">
                      <img
                        src={cldPresets.homeFeature(resolveUrl(rImg))}
                        alt={r.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                        <h3 className="text-white font-bold">{r.title}</h3>
                        <p className="text-white/80 text-xs">{r.location} {r.year && `· ${r.year}`}</p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox (in-page photo viewer) */}
      {lightboxIndex !== null && (
        <Lightbox
          images={images}
          overlays={overlays}
          currentIndex={lightboxIndex}
          title={project.title}
          description={project.description}
          location={project.location}
          year={project.year}
          onClose={closeLightbox}
          onPrevious={prev}
          onNext={next}
        />
      )}
    </div>
  );
};

export default ProjectDetail;
